import { useState, useEffect, useRef } from 'react'
import { MOCK_STUDIO_CREATE_ACTIONS } from './mockStudioActions'
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  HomeIcon,
  PlusIcon,
  RectangleStackIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  UserCircleIcon,
  UserGroupIcon,
  TrashIcon,
  PresentationChartLineIcon,
  CalculatorIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  RectangleGroupIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  CommandLineIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  SwatchIcon,
  Cog6ToothIcon,
  ChartBarSquareIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'
import logoUrl from '../../assets/messaging-advisory-logo.png'

import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { canUseSalesEnablement } from '../../utils/adminAccess'
import { LEAD_QUALIFIER_DEV } from '../../utils/leadQualifierFlag'
import { Tooltip } from '../shared/Tooltip'

interface SidebarProps {
  onOpenRoi?: () => void
}

type NavItemColor = 'default' | 'blue' | 'purple' | 'emerald' | 'amber'

const colorMap: Record<NavItemColor, { active: string; icon: string }> = {
  default: {
    active: 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md',
    icon: 'bg-gray-100',
  },
  blue: {
    active: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md',
    icon: 'bg-blue-100',
  },
  purple: {
    active: 'bg-gradient-to-r from-violet-600 to-purple-700 text-white shadow-md',
    icon: 'bg-violet-100',
  },
  emerald: {
    active: 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md',
    icon: 'bg-emerald-100',
  },
  amber: {
    active: 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md',
    icon: 'bg-amber-100',
  },
}

function NavItem({
  to,
  icon: Icon,
  children,
  color = 'default',
  end,
}: {
  to: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  children: React.ReactNode
  color?: NavItemColor
  end?: boolean
}) {
  const { active: activeClass, icon: iconBg } = colorMap[color]
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all ${
          isActive ? activeClass : 'text-gray-700 hover:bg-white hover:shadow-sm'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-colors flex-shrink-0 ${
            isActive ? 'bg-white/20' : iconBg
          }`}>
            <Icon className="w-4 h-4" />
          </div>
          {children}
        </>
      )}
    </NavLink>
  )
}

function MockNavLink({
  to,
  icon: Icon,
  children,
}: {
  to: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all ${
          isActive
            ? 'bg-blue-50 text-blue-700 font-semibold border border-blue-100'
            : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
          <span className="flex-1">{children}</span>
        </>
      )}
    </NavLink>
  )
}

function SalesNavLink({
  to,
  icon: Icon,
  step,
  children,
}: {
  to: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  step?: number
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all ${
          isActive
            ? 'bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100'
            : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
          <span className="flex-1">{children}</span>
          {step !== undefined && (
            <span className={`text-[10px] font-bold tabular-nums ${isActive ? 'text-emerald-500' : 'text-gray-300'}`}>
              {step}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}

function HealthDot({ score }: { score: number | null }) {
  if (score === null) return <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" title="Health: unknown" />
  if (score >= 90) return <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" title={`Health: ${score}/100`} />
  if (score >= 70) return <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" title={`Health: ${score}/100`} />
  return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title={`Health: ${score}/100`} />
}

export default function Sidebar({ onOpenRoi }: SidebarProps) {
  const { user, isManager } = useAuth()
  const [salesOpen, setSalesOpen] = useState(true)
  const [mockStudioOpen, setMockStudioOpen] = useState(true)
  const [_adminOpen] = useState(true)
  const [platformAdminOpen, setPlatformAdminOpen] = useState(true)
  const [helpOpen, setHelpOpen] = useState(false)
  const [healthScore, setHealthScore] = useState<number | null>(null)
  const helpMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  const company = user?.organization_name || 'Your Organization'
  const isPlatformAdmin = user?.is_super_admin
  const isOrgAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.is_super_admin
  const hasSalesAccess = canUseSalesEnablement(user)

  const navigate = useNavigate()

  // Context switcher — only for super-admins who have both Platform and Workspace
  const [context, setContext] = useState<'platform' | 'workspace'>(() =>
    location.pathname.startsWith('/platform-admin') ? 'platform' : 'workspace'
  )

  // Flipping the context must also navigate to that context's home, otherwise the
  // sidebar menu changes but the main content stays on the previous dashboard.
  const switchContext = (next: 'platform' | 'workspace') => {
    setContext(next)
    navigate(next === 'platform' ? '/platform-admin' : '/')
  }

  // Auto-sync context with URL so direct links keep the nav in the right mode
  useEffect(() => {
    if (location.pathname.startsWith('/platform-admin')) setContext('platform')
    else if (location.pathname.startsWith('/tenant-admin')) setContext('workspace')
  }, [location.pathname])

  const roleLabel = isPlatformAdmin
    ? 'Platform Admin'
    : user?.role === 'owner'
    ? 'Owner'
    : user?.role === 'admin'
    ? 'Admin'
    : user?.job_title || user?.role || 'Member'

  useEffect(() => {
    if (!isPlatformAdmin) return
    const poll = async () => {
      try {
        const res = await api.get('/platform-admin/health')
        setHealthScore(res.data?.score ?? null)
      } catch {
        setHealthScore(null)
      }
    }
    poll()
    const id = setInterval(poll, 30000)
    return () => clearInterval(id)
  }, [isPlatformAdmin])

  return (
    <div className="relative z-30 w-64 bg-gradient-to-b from-gray-50 to-white border-r border-gray-200 flex flex-col shadow-sm">
      {/* Logo */}
      <div className="p-6 pb-4 border-b border-gray-100">
        <Link to="/" className="block group">
          <img
            src={logoUrl}
            alt={company}
            className="h-12 w-auto mx-auto mb-3 object-contain group-hover:scale-105 transition-transform"
          />
          <div className="text-center">
            <p className="text-xs font-medium text-gray-600">{company}</p>
          </div>
        </Link>
      </div>

      {/* User card */}
      <div className="px-4 py-3">
        <div className={`flex items-center gap-3 p-3 rounded-xl border shadow-sm ${
          isPlatformAdmin
            ? 'bg-blue-50 border-blue-100'
            : 'bg-white border-gray-200'
        }`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isPlatformAdmin
              ? 'bg-gradient-to-br from-blue-500 to-blue-600'
              : 'bg-gradient-to-br from-violet-100 to-violet-200'
          }`}>
            <span className={`font-semibold text-sm ${isPlatformAdmin ? 'text-white' : 'text-violet-700'}`}>
              {user?.first_name?.charAt(0).toUpperCase() || user?.full_name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.full_name}
            </p>
            <p className={`text-xs truncate font-medium ${isPlatformAdmin ? 'text-blue-600' : 'text-gray-500'}`}>
              {roleLabel}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto pb-2">

        {/* ── Context switcher (super-admin only) ── */}
        {isPlatformAdmin && (
          <div className="mb-3 mt-1 flex items-center gap-1 rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => switchContext('platform')}
              className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all ${
                context === 'platform'
                  ? 'bg-white text-violet-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Platform
            </button>
            <button
              type="button"
              onClick={() => switchContext('workspace')}
              className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all ${
                context === 'workspace'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Workspace
            </button>
          </div>
        )}

        {/* ── Dashboard (always first) ── */}
        <div className="mb-1">
          <NavItem to="/" icon={HomeIcon} end>
            Dashboard
          </NavItem>
        </div>

        {/* ── PLATFORM CONTEXT ── */}
        {isPlatformAdmin && context === 'platform' && (
          <div className="mb-2">
            <div className="mt-1 flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-xs font-bold text-violet-700 uppercase tracking-wider">Platform Admin</span>
                <HealthDot score={healthScore} />
              </div>
              <Tooltip content={platformAdminOpen ? 'Collapse Platform Admin' : 'Expand Platform Admin'} position="right">
                <button
                  type="button"
                  onClick={() => setPlatformAdminOpen(o => !o)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-violet-300 hover:bg-violet-50 hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                  aria-label={platformAdminOpen ? 'Collapse Platform Admin' : 'Expand Platform Admin'}
                >
                  <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${platformAdminOpen ? 'rotate-180' : ''}`} />
                </button>
              </Tooltip>
            </div>
            {platformAdminOpen && (
              <div className="ml-3 pl-2 border-l-2 border-violet-100 space-y-0.5">
                <NavItem to="/platform-admin" icon={ChartBarIcon} color="blue" end>
                  Overview
                </NavItem>
                <NavItem to="/platform-admin/organizations" icon={BuildingOfficeIcon} color="blue">
                  Organizations
                </NavItem>
                <NavItem to="/platform-admin/users" icon={UsersIcon} color="blue">
                  All Users
                </NavItem>
                <NavItem to="/platform-admin/ai-usage" icon={CpuChipIcon} color="blue">
                  AI Usage
                </NavItem>
                <NavItem to="/platform-admin/audit-log" icon={ClipboardDocumentListIcon} color="blue">
                  Audit Log
                </NavItem>
                <NavItem to="/platform-admin/prompts" icon={CommandLineIcon} color="blue">
                  Prompt Library
                </NavItem>
                <NavItem to="/platform-admin/animation-tuning" icon={SparklesIcon} color="blue">
                  Animation Tuning
                </NavItem>
                <NavItem to="/platform-admin/session-security" icon={ClockIcon} color="blue">
                  Session Security
                </NavItem>
              </div>
            )}
            {/* Org shortcut — always visible in Platform context */}
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-0.5">
              <p className="px-3 pb-1 text-xs font-bold text-gray-400 uppercase tracking-wider">My Org</p>
              <NavItem to="/tenant-admin" icon={BuildingOffice2Icon} color="purple" end>
                Org Dashboard
              </NavItem>
              <NavItem to="/tenant-admin/settings" icon={Cog6ToothIcon} color="purple">
                Org Settings
              </NavItem>
              <NavItem to="/tenant-admin/quota" icon={ChartBarSquareIcon} color="purple">
                Usage & Quotas
              </NavItem>
            </div>
          </div>
        )}

        {/* ── WORKSPACE CONTEXT (or non-super-admin) ── */}
        {(!isPlatformAdmin || context === 'workspace') && (
          <>
            {/* Organization Admin hub */}
            {isOrgAdmin && (
              <div className="mb-2 space-y-0.5">
                <p className="px-3 pt-1 pb-0.5 text-xs font-bold text-violet-600 uppercase tracking-wider">
                  Administration
                </p>
                <NavItem to="/tenant-admin" icon={BuildingOffice2Icon} color="purple" end>
                  Org Dashboard
                </NavItem>
                <NavItem to="/tenant-admin/settings" icon={Cog6ToothIcon} color="purple">
                  Org Settings
                </NavItem>
              </div>
            )}
          </>
        )}

        {/* ── Mock Studio (workspace context only, or non-super-admin) ── */}
        {(!isPlatformAdmin || context === 'workspace') && (
          <>
            <div className="mt-1 flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Mock Studio</span>
              </div>
              <Tooltip content={mockStudioOpen ? 'Collapse Mock Studio' : 'Expand Mock Studio'} position="right">
                <button
                  type="button"
                  onClick={() => setMockStudioOpen(o => !o)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-blue-300 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label={mockStudioOpen ? 'Collapse Mock Studio' : 'Expand Mock Studio'}
                >
                  <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${mockStudioOpen ? 'rotate-180' : ''}`} />
                </button>
              </Tooltip>
            </div>

        {mockStudioOpen && (
            <div className="ml-3 pl-2 border-l-2 border-blue-100 space-y-0.5">
              <MockNavLink to="/brands" icon={RectangleStackIcon}>Brands</MockNavLink>
              <MockNavLink to="/use-cases" icon={ChatBubbleLeftRightIcon}>Use Cases</MockNavLink>
              <MockNavLink to="/templates" icon={SparklesIcon}>Templates</MockNavLink>
              <MockNavLink to="/figma-templates" icon={SwatchIcon}>Figma Templates</MockNavLink>
              <MockNavLink to="/collaboration" icon={UserGroupIcon}>Collaboration</MockNavLink>
              <MockNavLink to="/shared-links" icon={LinkIcon}>Shared Links</MockNavLink>
              <MockNavLink to="/tools/walkthrough" icon={PresentationChartLineIcon}>Walkthroughs</MockNavLink>
              <MockNavLink to="/recycle-bin" icon={TrashIcon}>Recycle Bin</MockNavLink>
            </div>
            )}

            {/* Create actions stay OUTSIDE the collapse.
                Collapsing Mock Studio is how you make room for the rest of the
                sidebar, not how you say "I am done creating things". Hiding the
                two entry points behind the same toggle meant the most common
                action in the product required expanding a section first. They
                keep the section's left rule so they still read as belonging to
                Mock Studio, and sit between it and Sales Enablement whether the
                section is open or closed. */}
            <div className="ml-3 space-y-1.5 border-l-2 border-blue-100 pl-2 pt-1">
              {MOCK_STUDIO_CREATE_ACTIONS.map(action => (
                <Link
                  key={action.to}
                  to={action.to}
                  className="flex w-full items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  {action.label}
                </Link>
              ))}
            </div>

            {/* ── Sales Enablement (gated, untouched) ── */}
            {hasSalesAccess && (
              <>
                <div className="mt-2 flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Sales Enablement</span>
                  </div>
                  <Tooltip content={salesOpen ? 'Collapse Sales Enablement' : 'Expand Sales Enablement'} position="right">
                    <button
                      type="button"
                      onClick={() => setSalesOpen((o) => !o)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-emerald-300 hover:bg-emerald-50 hover:text-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      aria-label={salesOpen ? 'Collapse Sales Enablement' : 'Expand Sales Enablement'}
                    >
                      <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${salesOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </Tooltip>
                </div>

                {salesOpen && (
                  <div className="ml-3 pl-2 border-l-2 border-emerald-100 space-y-0.5">
                    <SalesNavLink to="/sales-engagement" icon={PresentationChartLineIcon}>
                      Overview
                    </SalesNavLink>
                    {LEAD_QUALIFIER_DEV && (
                      <SalesNavLink to="/sales-engagement/lead-qualifier" icon={MagnifyingGlassIcon}>
                        Lead Qualifier (beta)
                      </SalesNavLink>
                    )}
                    <SalesNavLink to="/sales-engagement/account-research" icon={MagnifyingGlassIcon} step={1}>
                      Account Research
                    </SalesNavLink>
                    <SalesNavLink to="/sales-engagement/discovery" icon={ChatBubbleLeftRightIcon} step={2}>
                      Discovery Guide
                    </SalesNavLink>
                    <SalesNavLink to="/sales-engagement/industry-discovery" icon={ChartBarIcon} step={3}>
                      Industry Discovery
                    </SalesNavLink>
                    {onOpenRoi && (
                      <button
                        type="button"
                        onClick={onOpenRoi}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-600 rounded-lg transition-all hover:bg-emerald-50 hover:text-emerald-700"
                      >
                        <CalculatorIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        <span className="flex-1">ROI Calculator</span>
                        <span className="text-[10px] font-bold text-gray-300">4</span>
                      </button>
                    )}
                    <SalesNavLink to="/sales-engagement/use-case-builder" icon={DocumentTextIcon} step={5}>
                      Use Case Builder
                    </SalesNavLink>
                    <SalesNavLink to="/sales-engagement/value-projection" icon={LightBulbIcon} step={6}>
                      Value Projection
                    </SalesNavLink>
                    <SalesNavLink to="/sales-engagement/flow-designer" icon={RectangleGroupIcon} step={7}>
                      Flow Designer
                    </SalesNavLink>
                    {isManager && (
                      <SalesNavLink to="/sales-engagement/team" icon={UserGroupIcon}>
                        My Team
                      </SalesNavLink>
                    )}
                    <Link
                      to="/sales-engagement/account-research?new=1"
                      className="mt-1 flex w-full items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                    >
                      <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
                      Create New Deal
                    </Link>
                  </div>
                )}
              </>
            )}
          </>
        )}

      </nav>

      {/* Account */}
      <div className="px-3 pb-3 border-t border-gray-100 pt-3">
        <div className="flex items-center justify-between px-3 py-1 mb-1">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Account</span>
        </div>
        <NavItem to="/settings" icon={UserCircleIcon}>
          Settings
        </NavItem>
        <div
          ref={helpMenuRef}
          className="relative"
          onMouseEnter={() => setHelpOpen(true)}
          onMouseLeave={() => setHelpOpen(false)}
          onFocusCapture={() => setHelpOpen(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setHelpOpen(false)
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setHelpOpen(false)
              helpMenuRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
            }
          }}
        >
          <button
            type="button"
            onClick={() => setHelpOpen((open) => !open)}
            className={`flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
              helpOpen
                ? 'bg-indigo-700 text-white shadow-md'
                : 'bg-indigo-50 text-indigo-900 ring-1 ring-inset ring-indigo-100 hover:bg-indigo-700 hover:text-white hover:shadow-md'
            }`}
            aria-haspopup="menu"
            aria-expanded={helpOpen}
            aria-controls="sidebar-help-menu"
          >
            <span className={`mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
              helpOpen ? 'bg-white/15 text-white' : 'bg-indigo-600 text-white'
            }`}>
              <QuestionMarkCircleIcon className="h-4 w-4" />
            </span>
            <span className="flex-1 text-left">Help</span>
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>

          <div
            id="sidebar-help-menu"
            role="menu"
            aria-label="Help and guidance"
            className={`absolute bottom-0 left-full z-50 w-80 pl-3 transition duration-150 ${
              helpOpen
                ? 'visible translate-x-0 opacity-100'
                : 'pointer-events-none invisible -translate-x-1 opacity-0'
            }`}
          >
            <div className="overflow-hidden rounded-xl border border-slate-700 bg-slate-950 text-slate-100 shadow-2xl">
              <div className="border-b border-slate-800 px-4 py-3">
                <p className="text-sm font-bold text-white">Help and guidance</p>
                <p className="mt-1 text-xs leading-5 text-slate-300">
                  Choose the quickest path for what you are trying to do.
                </p>
              </div>
              <div className="space-y-1 p-2">
                <Link
                  to="/help"
                  role="menuitem"
                  className="block rounded-lg bg-indigo-900 px-3 py-2.5 transition-colors hover:bg-indigo-600 focus:bg-indigo-600 focus:outline-none"
                >
                  <span className="block text-sm font-semibold text-white">Open user guide</span>
                  <span className="mt-0.5 block text-xs text-indigo-100">Learn the workflow, validation states, and render modes.</span>
                </Link>
                <Link
                  to="/use-cases?create=1"
                  role="menuitem"
                  className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-indigo-600 focus:bg-indigo-600 focus:outline-none"
                >
                  <span className="block text-sm font-semibold text-white">Build a use case</span>
                  <span className="mt-0.5 block text-xs text-slate-300">Create a flow and validate it before rendering.</span>
                </Link>
                <Link
                  to="/templates"
                  role="menuitem"
                  className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-indigo-600 focus:bg-indigo-600 focus:outline-none"
                >
                  <span className="block text-sm font-semibold text-white">Browse templates</span>
                  <span className="mt-0.5 block text-xs text-slate-300">Start from a reusable element or complete flow.</span>
                </Link>
                <a
                  href="mailto:support@messagingadvisory.com"
                  role="menuitem"
                  className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-indigo-600 focus:bg-indigo-600 focus:outline-none"
                >
                  <span className="block text-sm font-semibold text-white">Contact support</span>
                  <span className="mt-0.5 block text-xs text-slate-300">Email us when you need a person to help.</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <a
          href="mailto:support@messagingadvisory.com"
          className="flex items-center px-3 py-2.5 text-sm font-medium rounded-xl text-gray-700 hover:bg-white hover:shadow-sm transition-all"
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 bg-gray-100">
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
          </div>
          Contact Support
        </a>
      </div>
    </div>
  )
}
